import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import type { UiState } from 'instantsearch.js';
import { useMemo } from 'react';
import { InstantSearchProps } from 'react-instantsearch';
import TypesenseInstantSearchAdapter, { type CollectionSearchParameters } from 'typesense-instantsearch-adapter';

import { useSearchContext } from '../../components/SearchContextProvider';
import { RouteState } from '../../hooks/useSearchRouter/useSearchRouter.ts';
import { appConfig } from '~/common/config';
import { useCurrentUser } from '~/hooks/useCurrentUser/useCurrentUser.ts';
import { useSearchDomains } from '~/routes/Search/hooks/useSearchDomains/useSearchDomains.ts';
import { SearchConfigGetter } from '~/routes/Search/types/SearchConfigGetter.ts';
import { SearchPresetSuffix } from '~/routes/Search/types/SearchPresetSuffixDomain.ts';

interface UseSearchClientProps {
  searchConfigByDomain: SearchConfigGetter;
}
interface UseSearchClientResult {
  searchClient: InstantSearchProps<UiState, RouteState>['searchClient'];
}

export const useSearchClient = ({ searchConfigByDomain }: UseSearchClientProps): UseSearchClientResult => {
  const { typesenseApiKey } = useCurrentUser();
  const { searchDomains } = useSearchDomains();
  const { hasPermission } = usePermissions();
  const { aiSearchIsEnabled } = useSearchContext();

  const hasSearchUseAiPermission = hasPermission(Permission.SearchUseAi);
  const hybridSearchEnabled = hasSearchUseAiPermission && aiSearchIsEnabled;

  return useMemo<UseSearchClientResult>(() => {
    const searchPresetSuffix = hybridSearchEnabled ? SearchPresetSuffix.SEMANTIC : SearchPresetSuffix.KEYWORD;

    return new TypesenseInstantSearchAdapter({
      server: {
        apiKey: typesenseApiKey,
        sendApiKeyAsQueryParam: false,
        nodes: [{ url: appConfig.aisearch.url }],
        // Cache search results from server. Defaults to 2 minutes. Set to 0 to disable caching.
        cacheSearchResultsForSeconds: 2 * 60,
      },
      collectionSpecificSearchParameters: searchDomains.reduce((acc, searchDomain) => {
        const config = searchConfigByDomain(searchDomain);
        return {
          ...acc,
          [config.indexName]: {
            preset: `${config.indexName}_${searchPresetSuffix}`,
            group_by: config.groupBy,
            group_limit: config.groupLimit,
          },
        };
      }, {} as CollectionSearchParameters),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hybridSearchEnabled, typesenseApiKey, searchDomains]);
};
